import _ from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import { Grid, Stack, TextField, Typography, Paper } from '@mui/material'
import RestoreIcon from '@mui/icons-material/Restore'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ArchiveIcon from '@mui/icons-material/Archive'

import colors from '@/assets/scss/_themes-vars.module.scss'
import NoResult from '@/assets/svg/no-transcription.svg'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import AudioViewer from '@/components/ui/preview/AudioViewer'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import TabPanel from '@/components/ui/tab/TabPanel'

import { ConsultationStatus } from '@/core/models/consultation.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  getConsultation,
  selectConsultationById,
  selectConsultationGetRequestTracker,
  selectConsultationUpdateRequestTracker,
  updateConsultation,
} from '@/redux/reducers/consultation.reducer'
import { createExpertise, selectExpertiseCreateRequestTracker, selectNewlyCreatedExpertiseId } from '@/redux/reducers/expertise.reducer'

import { isStaff } from '@/utils/type-guards'

import { ConsultationHeaderProps } from './components/ConsultationHeader'
import Messenger from './containers/Messenger'
import PatientProfile from './components/PatientProfile'
import VitalData from './containers/VitalData'
import Documents from '../diagnostic/containers/Documents'
import BottomBarIconButton from '../diagnostic/components/BottomBarIconButton'
import MobileConsultationHeader from './components/MobileConsultationHeader'
import ContentHeader from '../diagnostic/components/ContentHeader'

type ConsultationPageProps = {}

export default function MobileConsultationPage(props: ConsultationPageProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { consultationId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const tabs = useMemo(
    () => [
      { name: 'patientProfile', label: t('page.consultation.tab.patientProfile'), icon: RestoreIcon },
      { name: 'vitalData', label: t('page.consultation.tab.vitalData'), icon: FavoriteIcon },
      { name: 'documents', label: t('page.consultation.tab.documents'), icon: ArchiveIcon },
      { name: 'transcriptions', label: t('page.consultation.tab.transcriptions'), icon: RestoreIcon },
    ],
    [t]
  )
  const [currentTab, setCurrentTab] = useState<string>(tabs[0].name)

  const authUser = useAppSelector(selectAuthUser)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const getConsultationRequestTracker = useAppSelector(selectConsultationGetRequestTracker)
  const newlyCreatedExpertiseId = useAppSelector(selectNewlyCreatedExpertiseId)

  const dictaphones = useMemo(() => {
    return (
      consultation?.dictaphones.map((dictaphone) => ({
        filename: dictaphone.filename,
        key: dictaphone.key,
        src: dictaphone.url,
        text: dictaphone.text ?? '',
      })) ?? []
    )
  }, [consultation])

  const updateConsultationRequestHandler = useRequestHandler({
    onSuccess: () => enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' }),
    onError: () => enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const { dispatch: createExpertiseDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.expertise.create.success'), { variant: 'success' })

      navigate(`/expertises/${newlyCreatedExpertiseId}`)
    },
    onError: () => enqueueSnackbar(t('notification.consultation.end.error'), { variant: 'error' }),
    requestTrackerSelector: selectExpertiseCreateRequestTracker,
  })

  const renderMenuItems = () => {
    return tabs.map((menuItem) => (
      <Box key={menuItem.name}>
        <BottomBarIconButton
          icon={menuItem.icon}
          label={menuItem.label}
          onClick={() => setCurrentTab(menuItem.name)}
          selected={currentTab === menuItem.name}
        />
      </Box>
    ))
  }

  useEffect(() => {
    if (!consultationId || consultation) return

    dispatch(getConsultation(consultationId))
  }, [dispatch, consultationId, consultation])

  useEffect(() => {
    if (consultationId) {
      dispatch(getConsultation(consultationId))
    }
  }, [dispatch, consultationId])

  useEffect(() => {
    const action = searchParams.get('action')

    if (!action || !consultation) return

    let status: ConsultationStatus | undefined
    if (consultation.status === ConsultationStatus.Scheduled) {
      switch (action) {
        case 'accept':
          status = ConsultationStatus.Accepted
          break
        case 'refuse':
          status = ConsultationStatus.Refused
          break
        default:
          break
      }
    }

    searchParams.delete('action')
    setSearchParams(searchParams.toString())

    if (!!status) dispatch(updateConsultation(consultation._id, { status }))
  }, [consultation, dispatch, searchParams, setSearchParams])

  const handleGenerateExpertise = useCallback(() => {
    if (consultationId) {
      createExpertiseDispatch(
        createExpertise({
          expertise: {
            patient: consultation?.patient._id,
            consultations: [consultationId],
          },
        })
      )
    }
  }, [consultation, consultationId, createExpertiseDispatch])

  const handleCancelConsultation = useCallback(() => {
    if (consultationId) updateConsultationRequestHandler.dispatch(updateConsultation(consultationId, { status: ConsultationStatus.Canceled }))
  }, [consultationId, updateConsultationRequestHandler])

  const menuActions: ConsultationHeaderProps['actions'] = useMemo(() => {
    if (!consultation) return []
    const actions = [
      {
        label: t('page.consultation.actions.generateExpertise'),
        actionHandler: handleGenerateExpertise,
        fontColor: 'black',
        status: [ConsultationStatus.Completed],
      },
      {
        label: t('page.consultation.actions.join'),
        actionHandler: () => navigate(`live`),
        fontColor: 'black',
        status: [ConsultationStatus.Accepted, ConsultationStatus.Active, ConsultationStatus.Scheduled],
      },
      {
        label: t('page.consultation.actions.cancel'),
        actionHandler: handleCancelConsultation,
        fontColor: 'red',
        status: [ConsultationStatus.Accepted, ConsultationStatus.Scheduled],
      },
    ]
    return actions.filter((action) => action.status.includes(consultation.status)).map((action) => _.omit(action, ['status']))
  }, [consultation, navigate, t, handleCancelConsultation, handleGenerateExpertise])

  if (!consultation) {
    return !getConsultationRequestTracker.loading ? null : (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  return (
    <><Container maxWidth="xl" >
      <Box sx={{ pt: '20px', pb: '70px' }}>
        <Grid container>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <Grid alignItems="center" justifyContent="space-between" spacing={1}>
              <MobileConsultationHeader actions={menuActions} isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'} consultation={consultation} />
            </Grid>
          </Grid>
          <Box sx={{ mt: '28px', width: '100%' }}>
            <Grid container spacing={0} sx={{ width: '100%' }}>
              <Grid item xs={12} sx={{ width: '100%' }}>
                <TabPanel value="patientProfile" index={currentTab}>
                  <PatientProfile consultation={consultation} />
                </TabPanel>
                <TabPanel value="vitalData" index={currentTab}>
                  <VitalData consultationId={consultation._id} />
                </TabPanel>
                <TabPanel value="documents" index={currentTab}>
                  <Documents consultationId={consultation._id} />
                </TabPanel>
                <TabPanel value="transcriptions" index={currentTab}>
                  <Paper
                    sx={[
                      {
                        width: '100%',
                        minHeight: '300px',
                        p: '24px',
                      },
                    ]}
                  >
                    <Stack spacing={3} width='100%'>
                      <ContentHeader title={t('page.consultation.tab.transcriptions')} />
                      {dictaphones?.map((dictaphone) => (
                        <Stack key={dictaphone.key} spacing={2}>
                          <AudioViewer src={dictaphone.src} sx={{ width: '100%' }} />
                          <TextField fullWidth InputProps={{ readOnly: true }} minRows={4} multiline={true} defaultValue={dictaphone.text} />
                        </Stack>
                      ))}
                      {!dictaphones && <Stack alignItems="center" justifyContent="center" p="80px 24px">
                        <Typography> No transcriptions </Typography>
                        {/* <img alt="no-data" src={NoResult} />*/}
                      </Stack>}
                    </Stack>
                  </Paper>
                  <Stack spacing={3} width='100%' pt={3}>
                    <ContentHeader title={t('page.consultation.tab.conversation')} />
                    <Messenger consultationStatus={consultation.status} chatRoomId={consultation.chatRoom} />
                  </Stack>
                </TabPanel>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </Container>
      <Stack
        sx={{
          position: 'fixed',
          bottom: 0,
          backgroundColor: 'white',
          width: '100%',
          height: '56px',
          borderTop: '1px solid #E0E0E0',
          zIndex: 1000,
        }}
        direction="row"
        alignItems="center"
        justifyContent="space-evenly"
      >
        {renderMenuItems()}
      </Stack>
    </>
  )
}
